import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { DurationMetric, SentinelAnalyticsSeriesPoint } from '@/types/sentinelAnalytics';
import { cn } from '@/lib/utils';
import { formatWait } from './format';
import { MetricCardFrame } from '@/modules/analytics-v2/components/metric-card';
import { TrendChart } from './trend-chart';

type TimeMode = 'business' | 'calendar';

const MODE_LABEL: Record<TimeMode, string> = { business: 'Business hours', calendar: 'Calendar hours' };
const NO_BUSINESS_HOURS = 'Business hours aren’t set for this brand, so only calendar time is shown.';

interface DurationCardProps {
  title: string;
  help: string;
  metric: DurationMetric;
  businessAvailable: boolean;
  averageLabel?: string;
  /** Daily average, in minutes, per time mode; the chart follows the Business / Calendar toggle. */
  trend?: { series: SentinelAnalyticsSeriesPoint[]; value: Record<TimeMode, (point: SentinelAnalyticsSeriesPoint) => number | null> };
}

export function DurationCard({ title, help, metric, businessAvailable, averageLabel = 'Average', trend }: DurationCardProps) {
  const [selectedMode, setSelectedMode] = useState<TimeMode>('business');
  const mode: TimeMode = businessAvailable ? selectedMode : 'calendar';
  const stats = metric[mode];
  // The clock-time counterpart, so a business-hours zero can read "Off-hours".
  const clock = mode === 'business' ? metric.calendar : null;
  const rows: Array<[string, number | undefined, number | undefined]> = [
    [averageLabel, stats?.averageMs, clock?.averageMs],
    ['Typical (P 50)', stats?.p50Ms, clock?.p50Ms],
    ['Slowest 10% (P 90)', stats?.p90Ms, clock?.p90Ms],
  ];
  const toggle = (
    <div className='flex rounded-[8px] bg-gray-100 p-0.5'>
      {(['business', 'calendar'] as const).map((option) => {
        const disabled = option === 'business' && !businessAvailable;
        const button = (
          <button
            key={option}
            type='button'
            aria-pressed={mode === option}
            aria-disabled={disabled}
            aria-label={disabled ? `${MODE_LABEL[option]} (not set for this brand)` : undefined}
            onClick={() => !disabled && setSelectedMode(option)}
            className={cn(
              'rounded-[8px] border border-transparent px-[9px] py-px text-[11px] leading-4 text-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1',
              mode === option && 'border-gray-500 bg-white text-ink-strong',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            {MODE_LABEL[option]}
          </button>
        );
        // A disabled button swallows hover, so the tooltip hangs off an aria-disabled one that stays focusable.
        return disabled ? (
          <Tooltip key={option}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent variant='dark' side='top' sideOffset={5} className='max-w-[240px] rounded-md px-3 py-2 text-left text-xs leading-relaxed'>{NO_BUSINESS_HOURS}</TooltipContent>
          </Tooltip>
        ) : (
          button
        );
      })}
    </div>
  );
  return (
    <MetricCardFrame title={title} help={help} action={toggle}>
      {/* With no samples the rows still show, as dashes, so the card keeps its shape. */}
      <dl className='mt-5 flex flex-col gap-1.5'>
        {rows.map(([label, value, clockValue], index) => (
          <div key={label} className={cn('flex h-12 items-center justify-between gap-3', index > 0 && 'h-[55px] border-t border-dashed border-gray-300 pt-1.5')}>
            <dt className='text-sm font-medium text-gray-900'>{label}</dt>
            <dd className='text-xl font-semibold leading-7 tabular-nums text-ink'>{value === undefined ? '—' : formatWait(value, clockValue)}</dd>
          </div>
        ))}
      </dl>
      {stats && trend ? <TrendChart series={trend.series} value={trend.value[mode]} unit='minutes' label={`${title} daily average`} /> : null}
    </MetricCardFrame>
  );
}
